import { useRef, useState } from 'react'
import html2canvas from 'html2canvas'
import { QRCodeSVG } from 'qrcode.react'
import {
  TYPE_INTERPRETATIONS,
  TYPE_COLORS,
  DIMENSION_INFO,
  type TypeCode,
  type TypeComparison,
} from '@mindtype/shared'

// 维度简称映射
const DIMENSION_SHORT: Record<string, { A: string; B: string }> = {
  D1: { A: '内部', B: '外部' },
  D2: { A: '系统', B: '情境' },
  D3: { A: '内在', B: '外在' },
  D4: { A: '闭合', B: '探索' },
}

interface CompareShareCardProps {
  typeA: TypeCode
  typeB: TypeCode
  comparison: TypeComparison
  onClose: () => void
}

export function CompareShareCard({ typeA, typeB, comparison, onClose }: CompareShareCardProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  const [downloading, setDownloading] = useState(false)

  const colorA = TYPE_COLORS[typeA]
  const colorB = TYPE_COLORS[typeB]
  const typeInfoA = TYPE_INTERPRETATIONS[typeA]
  const typeInfoB = TYPE_INTERPRETATIONS[typeB]

  const handleDownload = async () => {
    if (!cardRef.current) return

    setDownloading(true)
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
      })
      const link = document.createElement('a')
      link.download = `mindtype-compare-${typeA}-${typeB}.png`
      link.href = canvas.toDataURL('image/png')
      link.click()
    } catch (error) {
      console.error('Failed to generate image:', error)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full max-h-[90vh] flex flex-col">
        {/* 预览区域 - 可滚动 */}
        <div className="flex-1 overflow-y-auto p-3">
          <div
            ref={cardRef}
            className="bg-white relative"
            style={{
              width: '100%',
              aspectRatio: '3 / 4',
            }}
          >
            {/* 顶部渐变装饰 */}
            <div
              className="absolute top-0 left-0 right-0 h-16"
              style={{
                background: `linear-gradient(135deg, ${colorA.primary}15 0%, ${colorB.primary}15 100%)`,
              }}
            />

            {/* 主要内容 */}
            <div className="relative z-10 px-4 pt-4 pb-3">
              {/* Logo */}
              <div className="text-center mb-3">
                <h1 className="text-sm font-bold text-gray-800">MindType</h1>
                <p className="text-[10px] text-gray-500">认知类型对比</p>
              </div>

              {/* 双类型展示区 */}
              <div className="flex items-center justify-center gap-3 mb-3">
                {/* 类型 A */}
                <div className="text-center flex-1">
                  <div
                    className="inline-block rounded-md px-3 py-1.5 mb-1"
                    style={{ backgroundColor: colorA.primary }}
                  >
                    <span
                      className="text-lg font-bold tracking-wide"
                      style={{ color: colorA.text }}
                    >
                      {typeA}
                    </span>
                  </div>
                  <p
                    className="text-[10px] font-medium"
                    style={{ color: colorA.primary }}
                  >
                    {typeInfoA?.name}
                  </p>
                </div>

                {/* VS */}
                <div className="text-gray-300 text-sm font-light">×</div>

                {/* 类型 B */}
                <div className="text-center flex-1">
                  <div
                    className="inline-block rounded-md px-3 py-1.5 mb-1"
                    style={{ backgroundColor: colorB.primary }}
                  >
                    <span
                      className="text-lg font-bold tracking-wide"
                      style={{ color: colorB.text }}
                    >
                      {typeB}
                    </span>
                  </div>
                  <p
                    className="text-[10px] font-medium"
                    style={{ color: colorB.primary }}
                  >
                    {typeInfoB?.name}
                  </p>
                </div>
              </div>

              {/* 关系类型 */}
              <div
                className="text-center rounded-lg py-2 mb-3"
                style={{
                  background: `linear-gradient(135deg, ${colorA.primary}10 0%, ${colorB.primary}10 100%)`,
                }}
              >
                <p className="text-base font-bold text-gray-800">
                  {comparison.relationshipType}
                </p>
                <p className="text-[9px] text-gray-500 mt-0.5">
                  相同维度: {comparison.sameCount}/4
                </p>
              </div>

              {/* 四维度对比 */}
              <div className="bg-gray-50 rounded-lg p-3 mb-3">
                <div className="space-y-2">
                  {comparison.dimensionComparison.map((dim) => {
                    const shortNames = DIMENSION_SHORT[dim.dimension]
                    const info = DIMENSION_INFO[dim.dimension]

                    const typeAIsLeft = dim.typeALetter === info.poleA.letter
                    const typeBIsLeft = dim.typeBLetter === info.poleA.letter
                    const typeAPos = typeAIsLeft ? 20 : 80
                    const typeBPos = typeBIsLeft ? 20 : 80

                    // 根据是否相同决定颜色强度
                    const colorADisplay = dim.isSame ? colorA.primary : `${colorA.primary}60`
                    const colorBDisplay = dim.isSame ? colorB.primary : `${colorB.primary}60`

                    return (
                      <div key={dim.dimension}>
                        <div className="flex justify-between text-[8px] mb-1">
                          <span style={{
                            color: dim.isSame ? colorA.primary : '#9ca3af',
                            fontWeight: dim.isSame ? 600 : 400,
                          }}>
                            {shortNames.A}
                          </span>
                          <span
                            className="font-medium"
                            style={{ color: dim.isSame ? colorA.primary : '#9ca3af' }}
                          >
                            {dim.dimensionName}
                          </span>
                          <span style={{
                            color: dim.isSame ? colorB.primary : '#9ca3af',
                            fontWeight: dim.isSame ? 600 : 400,
                          }}>
                            {shortNames.B}
                          </span>
                        </div>
                        <div
                          className="relative h-2 rounded-full"
                          style={{
                            backgroundColor: dim.isSame ? `${colorA.primary}15` : '#e5e7eb',
                          }}
                        >
                          {/* 相同维度时显示渐变连接条 */}
                          {dim.isSame && (
                            <div
                              className="absolute top-1/2 -translate-y-1/2 h-1.5 rounded-full"
                              style={{
                                left: `calc(${Math.min(typeAPos, typeBPos)}% - 6px)`,
                                right: `calc(${100 - Math.max(typeAPos, typeBPos)}% - 6px)`,
                                background: `linear-gradient(90deg, ${colorA.primary} 0%, ${colorB.primary} 100%)`,
                              }}
                            />
                          )}
                          {/* 类型 A 标记 */}
                          <div
                            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded border border-white shadow-sm z-10"
                            style={{
                              left: `calc(${typeAPos}% - 6px)`,
                              backgroundColor: colorADisplay,
                            }}
                          />
                          {/* 类型 B 标记 */}
                          <div
                            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded border border-white shadow-sm z-10"
                            style={{
                              left: `calc(${typeBPos}% - 6px)`,
                              backgroundColor: colorBDisplay,
                            }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* 关系描述 - 简短版 */}
              <div className="px-2 mb-3">
                <p className="text-[9px] text-gray-600 text-center leading-relaxed">
                  {comparison.relationshipAnalysis.slice(0, 80)}...
                </p>
              </div>

              {/* 二维码 */}
              <div className="flex flex-col items-center pt-1">
                <QRCodeSVG
                  value={`https://mindtype.pages.dev/compare?a=${typeA}&b=${typeB}`}
                  size={48}
                  level="M"
                />
                <p className="text-[9px] text-gray-500 mt-1">扫码查看详情</p>
              </div>
            </div>

            {/* 底部装饰 */}
            <div className="absolute bottom-0 left-0 right-0 h-10 overflow-hidden">
              <div
                className="absolute -bottom-4 -left-4 w-14 h-14 rounded-full"
                style={{ backgroundColor: `${colorA.primary}10` }}
              />
              <div
                className="absolute -bottom-4 -right-4 w-14 h-14 rounded-full"
                style={{ backgroundColor: `${colorB.primary}10` }}
              />
            </div>
          </div>
        </div>

        {/* 操作按钮 - 固定在底部 */}
        <div className="border-t border-gray-100 p-3 flex gap-3 shrink-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-sm"
          >
            关闭
          </button>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="flex-1 px-4 py-2 text-white rounded-lg transition-opacity hover:opacity-90 disabled:opacity-50 text-sm"
            style={{
              background: `linear-gradient(135deg, ${colorA.primary} 0%, ${colorB.primary} 100%)`,
            }}
          >
            {downloading ? '生成中...' : '保存图片'}
          </button>
        </div>
      </div>
    </div>
  )
}
